import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IDependencyTelemetry } from '@microsoft/applicationinsights-common';
import { IPolicy } from '../Entities/policy';
import { Event, getValueEvent } from '../enums/insights/event';
import { getValueExceptionEvent } from '../enums/insights/exception-event';
import { getValuePageView, PageView } from '../enums/insights/page-view';
import { SeverityLevel } from '../enums/insights/severity-level';
import { Token } from '../helpers/token';
import { UserInfo } from '../helpers/user-info';
import { appInsights } from '../insights/appInsights';
import { DataInsights } from '../insights/data-insights';
import { EventType } from '../insights/event-type';
import { PROPERTIES_INSIGHTS } from '../insights/properties-insights';

@Injectable({ providedIn: 'root' })
export class AppInsightsService {
  public tokenData: Token;
  public lastTrackName: Event;
  public userInfo: UserInfo;
  public uuid: string;
  public additionalProperties: any;
  public pageViewParentActive: { viewName: string; label: string };
  public pageViewActive: { viewName: string; label: string };
  public policySelected: IPolicy;
  public policyId: string;

  public constructor(private router: Router) {}

  public trackEvent(
    name: Event,
    properties?: { [name: string]: string }
  ): void {
    try {
      this.lastTrackName = name;
      const customProperties: any = {
        ...this.mapDataToInsights(
          getValueEvent[name] ? getValueEvent[name] : name
        ),
        ...properties,
        eventType: EventType.functionalityEvent,
      };
      appInsights.trackEvent({ name, properties: customProperties });
    } catch (e) {
      console.warn('Angular application insights Error [trackEvent]: ', e);
    }
  }

  public trackPageView(
    name: PageView,
    uri?: string,
    properties?: { [name: string]: string },
    duration?: number
  ): void {
    try {
      appInsights.trackPageView({
        name,
        uri,
        pageType: PROPERTIES_INSIGHTS.pageType,
        isLoggedIn: true,
        properties: {
          duration: duration ? duration : null,
          ...this.mapDataToInsights(
            getValuePageView[name] ? getValuePageView[name] : name
          ),
          ...properties,
        },
      });
    } catch (e) {
      console.warn('Angular application insights Error [trackPageView]: ', e);
    }
  }

  public trackException(
    exception: Error,
    severityLevel: SeverityLevel,
    properties?: { [name: string]: string }
  ): void {
    try {
      const customProperties: any = {
        ...this.mapDataToInsights(
          getValueExceptionEvent[exception.name]
            ? getValueExceptionEvent[exception.name]
            : name
        ),
        ...properties,
        eventType: EventType.exceptionEvent,
        exceptionName: exception.name,
        error: exception.message,
      };
      appInsights.trackException({
        exception,
        severityLevel,
        properties: customProperties,
      });
    } catch (e) {
      console.warn('Angular application insights Error [trackException]: ', e);
    }
  }

  public trackExceptionWithError(
    exception: Error,
    severityLevel: SeverityLevel,
    errorTrace: any,
    properties?: { [name: string]: string }
  ): void {
    try {
      const customProperties: any = {
        ...this.mapDataToInsights(
          getValueExceptionEvent[exception.name]
            ? getValueExceptionEvent[exception.name]
            : name
        ),
        ...properties,
        eventType: EventType.exceptionEvent,
        exceptionName: exception.name,
        error: errorTrace,
      };
      appInsights.trackException({
        exception,
        severityLevel,
        properties: customProperties,
      });
    } catch (e) {
      console.warn('Angular application insights Error [trackException]: ', e);
    }
  }

  public authenticate(authenticatedUserId: string, accountId?: string): void {
    console.log('authenticatedUserId => ', authenticatedUserId);
    try {
      appInsights.setAuthenticatedUserContext(authenticatedUserId, accountId);
    } catch (e) {
      console.warn(
        'Angular application insights Error [setAuthenticatedUserContext]: ',
        e
      );
    }
  }

  public startTrackEvent(name?: Event): void {
    try {
      appInsights.startTrackEvent(name);
    } catch (e) {
      console.warn('Angular application insights Error [startTrackPage]: ', e);
    }
  }

  public stopTrackEvent(
    name?: Event,
    properties?: { [name: string]: string },
    measurements?: { [p: string]: number }
  ): void {
    try {
      appInsights.stopTrackEvent(
        name,
        {
          ...this.mapDataToInsights(
            getValueEvent[name] ? getValueEvent[name] : name
          ),
          ...properties,
          eventType: EventType.trackEvent,
        },
        measurements
      );
    } catch (e) {
      console.warn('Angular application insights Error [stopTrackPage]: ', e);
    }
  }

  public trackDependencyData({ ...props }: IDependencyTelemetry): void {
    try {
      appInsights.trackDependencyData({ ...props });
    } catch (e) {
      console.warn(
        'Angular application insights Error [trackDependencyData]: ',
        e
      );
    }
  }

  public mapDataToInsights(label: string): DataInsights {
    if (!this.userInfo) {
      return null;
    }

    const { name, email, business, policy } = this.userInfo;

    return {
      personName: name,
      email: email,
      business: business,
      policies: JSON.stringify(policy),
      pageViewName:
        this.pageViewActive && this.pageViewActive.viewName
          ? this.pageViewActive.viewName
          : this.router.url,
      pageViewLabel:
        this.pageViewActive && this.pageViewActive.label
          ? this.pageViewActive.label
          : '',
      pageViewParentName:
        this.pageViewParentActive && this.pageViewParentActive.viewName
          ? this.pageViewParentActive.viewName
          : this.router.url,
      pageViewParentLabel:
        this.pageViewParentActive && this.pageViewParentActive.label
          ? this.pageViewParentActive.label
          : '',
      flowId: this.uuid,
      sessionId:
        this.tokenData && this.tokenData.sessionState
          ? this.tokenData.sessionState
          : '',
      label,
      policy:
        this.policySelected && this.policySelected.policy
          ? this.policySelected.policy.toString()
          : this.policyId
          ? this.policyId
          : '',
      code:
        this.policySelected && this.policySelected.code
          ? this.policySelected.code
          : '',
      clientCode: this.policySelected ? this.policySelected.clientCode : '',
      clientId:
        this.policySelected && this.policySelected.clientId
          ? this.policySelected.clientId.toString()
          : '',
      policyId:
        this.policySelected && this.policySelected.policyId
          ? this.policySelected.policyId.toString()
          : '',
      product:
        this.policySelected && this.policySelected.name
          ? this.policySelected.name
          : '',
      businessLine:
        this.policySelected && this.policySelected.businessLine
          ? this.policySelected.businessLine
          : '',
      appName: PROPERTIES_INSIGHTS.pageType,
    };
  }
}
